import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { apiService, Scoreboard as ScoreboardData, ScoreboardCell } from '../services/api';

// Below this many weeks of picks a result is shown but not judged (pick_scoreboard_service.MIN_WEEKS)
const MIN_WEEKS = 8;
// Benjamini-Hochberg q under which a result counts as clear
const CLEAR = 0.05;
const HORIZON_TEXT: Record<string, string> = { '1m': '1 month', '3m': '3 months', '6m': '6 months' };

const signed = (v?: number | null) => (v == null ? '–' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
const pct = (v?: number | null) => (v == null ? '–' : `${Math.round(v)}%`);
const color = (v?: number | null) => (v == null ? undefined : v > 0 ? 'success.main' : v < 0 ? 'error.main' : undefined);

function Verdict({ c }: { c: ScoreboardCell }) {
  if (!c.matured) return <Chip size="small" label="no results yet" variant="outlined" />;
  if ((c.weeks ?? 0) < MIN_WEEKS) return <Chip size="small" label={`too early · ${c.weeks} wk`} variant="outlined" />;
  if (c.q != null && c.q < CLEAR) {
    const good = (c.vs_band_pct ?? 0) > 0;
    return <Chip size="small" color={good ? 'success' : 'error'} label={good ? 'clearly better' : 'clearly worse'} />;
  }
  return <Chip size="small" label="no clear edge" />;
}

export default function Scoreboard() {
  const [data, setData] = useState<ScoreboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hz, setHz] = useState('1m');

  useEffect(() => {
    apiService.getScoreboard().then(setData).catch(e => setError(e?.message || 'Failed to load'));
  }, []);

  const refresh = () => {
    setBusy(true);
    apiService.refreshScoreboard().then(setData).catch(e => setError(e?.message || 'Refresh failed')).finally(() => setBusy(false));
  };

  const rows = data ? Object.entries(data.sources) : [];
  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>Scoreboard</Typography>
        <Button variant="outlined" onClick={refresh} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : 'Snapshot and rescore now'}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Every page that names stocks is frozen once a day. Each <b>new</b> pick (a stock that was off that page
        for {data?.new_after_days ?? 30} days or more) is scored after 1, 3 and 6 months against SPY over the same days, and
        against <b>stocks of the same volatility</b>: all $5+ stocks with $3M+ a day, split into ten bands by how much they
        move, compared with the pick's own band. A volatile stock beating SPY in a rising market is not skill, because its
        whole band did too. History the app already kept (since May–August 2026) is included; Top Picks' consensus list
        is frozen from 30 October 2026.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <CircularProgress />}
      {data && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={hz} onChange={(_, v) => v && setHz(v)}>
              {Object.keys(data.horizons).map(h => <ToggleButton key={h} value={h}>{HORIZON_TEXT[h] ?? h}</ToggleButton>)}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              Scored {new Date(data.generated_at + 'Z').toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="right">Picks scored</TableCell>
                  <TableCell align="right">Average</TableCell>
                  <TableCell align="right">vs SPY</TableCell>
                  <TableCell align="right">vs same-volatility stocks</TableCell>
                  <TableCell align="right">Beat their band</TableCell>
                  <TableCell align="right">Fell 20%+</TableCell>
                  <TableCell>Verdict</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(([src, r]) => {
                  const c = r.horizons[hz] || { matured: 0 };
                  return (
                    <TableRow key={src} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.new_picks} new picks{r.first_pick ? ` since ${r.first_pick}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {c.matured || '–'}
                        {c.weeks != null && <Typography variant="caption" display="block" color="text.secondary">{c.weeks} weeks</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ color: color(c.avg_return_pct) }}>{signed(c.avg_return_pct)}</TableCell>
                      <TableCell align="right" sx={{ color: color(c.vs_spy_pct) }}>{signed(c.vs_spy_pct)}</TableCell>
                      <TableCell align="right" sx={{ color: color(c.vs_band_pct), fontWeight: 700 }}>{signed(c.vs_band_pct)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="How often any stock in the same band beat the band's average (under 50% because a few big winners lift the average)">
                          <span>{pct(c.beat_band_pct)} <Typography component="span" variant="caption" color="text.secondary">vs {pct(c.beat_band_base_pct)}</Typography></span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Share of picks that fell 20% or more, against the share of their band that did">
                          <span style={{ color: c.fell20_pct != null && c.fell20_base_pct != null && c.fell20_pct > c.fell20_base_pct * 1.5 ? '#EF4444' : undefined }}>
                            {pct(c.fell20_pct)} <Typography component="span" variant="caption" color="text.secondary">vs {pct(c.fell20_base_pct)}</Typography>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={c.q != null ? `q ${c.q} (Benjamini-Hochberg across every page and length); p ${c.p}` : 'Needs 8 weeks of picks before it is tested'}>
                          <span><Verdict c={c} /></span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            "Clearly" means it survived the multiple-testing check (q under 0.05) on weekly averages. Small samples swing
            wildly: a page with a few weeks of picks can look brilliant or terrible from one market move. Returns are
            price only; dividends are not included.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
